import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './StudentRegistration.css';
import { supabase } from '../../lib/supabaseClient';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { validatePasswordStrength, getPasswordErrorMessage } from '../../lib/passwordValidation';

const StudentRegistration: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    studentId: '',
    courseInfo: '',
    email: '',
    password: ''
  });

  // Calculate password strength
  const passwordStrength = validatePasswordStrength(formData.password);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate password strength before submission
    if (!passwordStrength.isValid) {
      setError(getPasswordErrorMessage(passwordStrength));
      setLoading(false);
      setPasswordTouched(true);
      return;
    }

    try {
      // Prepare registration data for user metadata
      const registrationData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        student_id: formData.studentId.trim(), // Ensure clean student ID
        course_info: formData.courseInfo,
        email: formData.email.toLowerCase().trim(), // Ensure clean email
        role: 'student'
      };
      
      // Log the data being stored to verify it
      console.log('Storing registration data:', registrationData);
      
      localStorage.setItem('pendingRegistration', JSON.stringify(registrationData));

      // 1. Create auth user with additional metadata
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            first_name: formData.firstName,
            last_name: formData.lastName,
            role: 'student'
          },
          emailRedirectTo: `${window.location.origin}/verify`
        }
      });

      if (signUpError) throw signUpError;

      if (authData.user) {
        // Show success message and redirect to confirmation page
        // After email verification, user will be redirected to /verify which will then redirect to /student-home
        navigate('/registration-success');
      }
    } catch (err) {
      console.error('Registration error:', err);
      if (err instanceof Error) {
        if (err.message.includes('duplicate key')) {
          setError('This email or student ID is already registered');
        } else if (err.message.includes('email')) {
          setError('Please check your email address');
        } else if (err.message.includes('password')) {
          setError('Password must be at least 6 characters long');
        } else {
          setError(err.message);
        }
      } else {
        setError('An error occurred during registration');
      }
    } finally {
      setLoading(false);
    }
  };

  const goToLogin = () => {
    window.location.href = '/student';
  };

  const goToSupervisorRegistration = () => {
    window.location.href = '/supervisor-registration';
  };

  return (
    <div className="login-container">
      <div className="login-box registration-box">
        <div className="logo">
          <h1>Intern Tr<FaMapMarkerAlt className="location-icon" />ckr</h1>
          <p className="tagline">Anytime. Anywhere.</p>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="input-row">
            <div className="input-group">
              <label>First Name</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
              <div className="label-description">Please enter your first name</div>
              {formData.firstName && (
                <button 
                  type="button" 
                  className="clear-button"
                  onClick={() => setFormData(prev => ({...prev, firstName: ''}))}
                >
                  ✕
                </button>
              )}
            </div>
            <div className="input-group">
              <label>Last Name</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
              />
              <div className="label-description">Please enter your surname</div>
              {formData.lastName && (
                <button 
                  type="button" 
                  className="clear-button"
                  onClick={() => setFormData(prev => ({...prev, lastName: ''}))}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          <div className="input-row">
            <div className="input-group">
              <label>Student ID</label>
              <input
                type="text"
                name="studentId"
                value={formData.studentId}
                onChange={handleChange}
                required
              />
              <div className="label-description">Please enter your student ID number</div>
              {formData.studentId && (
                <button 
                  type="button" 
                  className="clear-button"
                  onClick={() => setFormData(prev => ({...prev, studentId: ''}))}
                >
                  ✕
                </button>
              )}
            </div>
            <div className="input-group">
              <label>Course, Year, & Section</label>
              <input
                type="text"
                name="courseInfo"
                value={formData.courseInfo}
                onChange={handleChange}
                required
              />
              <div className="label-description">Please enter your course, year and section</div>
              {formData.courseInfo && (
                <button 
                  type="button" 
                  className="clear-button"
                  onClick={() => setFormData(prev => ({...prev, courseInfo: ''}))}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          <div className="input-row">
            <div className="input-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
              <div className="label-description">Please enter your email</div>
              {formData.email && (
                <button 
                  type="button" 
                  className="clear-button"
                  onClick={() => setFormData(prev => ({...prev, email: ''}))}
                >
                  ✕
                </button>
              )}
            </div>
            <div className="input-group">
              <label>Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                onBlur={() => setPasswordTouched(true)}
                required
              />
              <div className="label-description">Please enter a strong password</div>
              {formData.password && (
                <button 
                  type="button" 
                  className="clear-button"
                  onClick={() => setFormData(prev => ({...prev, password: ''}))}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          {/* Password Requirements Indicator */}
          {(formData.password || passwordTouched) && (
            <div className="password-requirements">
              <p className="requirements-title">Password must contain:</p>
              <ul>
                <li className={passwordStrength.hasMinLength ? 'valid' : 'invalid'}>
                  {passwordStrength.hasMinLength ? '✓' : '✗'} At least 8 characters
                </li>
                <li className={passwordStrength.hasUpperCase ? 'valid' : 'invalid'}>
                  {passwordStrength.hasUpperCase ? '✓' : '✗'} An uppercase letter (A-Z)
                </li>
                <li className={passwordStrength.hasLowerCase ? 'valid' : 'invalid'}>
                  {passwordStrength.hasLowerCase ? '✓' : '✗'} A lowercase letter (a-z)
                </li>
                <li className={passwordStrength.hasNumber ? 'valid' : 'invalid'}>
                  {passwordStrength.hasNumber ? '✓' : '✗'} A number (0-9)
                </li>
                <li className={passwordStrength.hasSpecialChar ? 'valid' : 'invalid'}>
                  {passwordStrength.hasSpecialChar ? '✓' : '✗'} A special character (!, @, #, $, etc.)
                </li>
              </ul>
            </div>
          )}
          {error && <div className="error-message">{error}</div>}
          <div className="button-group">
            <button type="submit" className="login-button register-button" disabled={loading}>
              {loading ? 'Registering...' : 'Register'}
            </button>
          </div>
        </form>
        <div className="links-section">
          <p>Already have an account? <button onClick={goToLogin} className="link-button">Click here to login.</button></p>
          <p>Not a student? <button onClick={goToSupervisorRegistration} className="link-button">Click here to register as Supervisor.</button></p>
        </div>
      </div>
    </div>
  );
};

export default StudentRegistration;
